import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { Produto, Cliente, Venda, Despesa, Aporte, Retirada, RetiradaProduto } from '@/mocks/store';
import { mockProdutos, mockClientes, mockVendas, mockDespesas, mockAportes, mockRetiradas, mockRetiradaProdutos } from '@/mocks/store';

export interface Categoria {
  id: string;
  nome: string;
}

// ─── Mappers: DB row → app type ───────────────────────────────────────────────

function rowToProduto(r: Record<string, unknown>): Produto {
  return {
    id: r.id as string,
    nome: r.nome as string,
    categoria: r.categoria as string,
    custo: Number(r.custo),
    custoExtra: Number(r.custo_extra),
    precoVenda: Number(r.preco_venda),
    margem: Number(r.margem),
    estoque: Number(r.estoque),
    status: r.status as 'ativo' | 'inativo',
    criadoEm: (r.criado_em as string) ?? '',
  };
}

function rowToCliente(r: Record<string, unknown>): Cliente {
  return {
    id: r.id as string,
    nome: r.nome as string,
    telefone: (r.telefone as string) ?? '',
  };
}

function rowToVenda(r: Record<string, unknown>): Venda {
  return {
    id: r.id as string,
    produtoId: (r.produto_id as string) ?? '',
    produtoNome: r.produto_nome as string,
    quantidade: Number(r.quantidade),
    valorTotal: Number(r.valor_total),
    formaPagamento: r.forma_pagamento as 'dinheiro' | 'pix' | 'cartao',
    taxaCartao: Number(r.taxa_cartao),
    valorLiquido: Number(r.valor_liquido),
    data: r.data as string,
    clienteId: (r.cliente_id as string) ?? undefined,
    clienteNome: (r.cliente_nome as string) ?? undefined,
    descontoPercentual: (r.desconto_percentual as number) ?? undefined,
    valorDesconto: (r.valor_desconto as number) ?? undefined,
  };
}

function rowToDespesa(r: Record<string, unknown>): Despesa {
  return {
    id: r.id as string,
    descricao: r.descricao as string,
    valor: Number(r.valor),
    categoria: r.categoria as string,
    data: r.data as string,
  };
}

function rowToAporte(r: Record<string, unknown>): Aporte {
  return {
    id: r.id as string,
    valor: Number(r.valor),
    data: r.data as string,
    observacao: (r.observacao as string) ?? '',
  };
}

function rowToRetirada(r: Record<string, unknown>): Retirada {
  return {
    id: r.id as string,
    valor: Number(r.valor),
    data: r.data as string,
  };
}

function rowToRetiradaProduto(r: Record<string, unknown>): RetiradaProduto {
  return {
    id: r.id as string,
    produtoId: (r.produto_id as string) ?? '',
    produtoNome: r.produto_nome as string,
    quantidade: Number(r.quantidade),
    custoUnitario: Number(r.custo_unitario),
    custoTotal: Number(r.custo_total),
    motivo: (r.motivo as string) ?? 'Uso pessoal',
    data: r.data as string,
  };
}

function rowToCategoria(r: Record<string, unknown>): Categoria {
  return { id: r.id as string, nome: r.nome as string };
}

// ─── Hook principal ────────────────────────────────────────────────────────────

export function useStore() {
  const [produtos, setProdutos] = useState<Produto[]>(mockProdutos);
  const [clientes, setClientes] = useState<Cliente[]>(mockClientes);
  const [vendas, setVendas] = useState<Venda[]>(mockVendas);
  const [despesas, setDespesas] = useState<Despesa[]>(mockDespesas);
  const [aportes, setAportes] = useState<Aporte[]>(mockAportes);
  const [retiradas, setRetiradas] = useState<Retirada[]>(mockRetiradas);
  const [retiradaProdutos, setRetiradaProdutos] = useState<RetiradaProduto[]>(mockRetiradaProdutos);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [supabaseReady, setSupabaseReady] = useState(false);

  // Carrega todos os dados do Supabase
  useEffect(() => {
    async function fetchAll() {
      setLoadingData(true);
      const [p, c, v, d, a, r, rp, cat] = await Promise.all([
        supabase.from('produtos').select('*').order('created_at', { ascending: false }),
        supabase.from('clientes').select('*').order('nome', { ascending: true }),
        supabase.from('vendas').select('*').order('created_at', { ascending: false }),
        supabase.from('despesas').select('*').order('created_at', { ascending: false }),
        supabase.from('aportes').select('*').order('created_at', { ascending: false }),
        supabase.from('retiradas').select('*').order('created_at', { ascending: false }),
        supabase.from('retiradas_produto').select('*').order('created_at', { ascending: false }),
        supabase.from('categorias').select('*').order('nome', { ascending: true }),
      ]);
      if (p.data) setProdutos(p.data.map(rowToProduto));
      if (c.data) setClientes(c.data.map(rowToCliente));
      if (v.data) setVendas(v.data.map(rowToVenda));
      if (d.data) setDespesas(d.data.map(rowToDespesa));
      if (a.data) setAportes(a.data.map(rowToAporte));
      if (r.data) setRetiradas(r.data.map(rowToRetirada));
      if (rp.data) setRetiradaProdutos(rp.data.map(rowToRetiradaProduto));
      if (cat.data) setCategorias(cat.data.map(rowToCategoria));
      setSupabaseReady(true);
      setLoadingData(false);
    }
    fetchAll();
  }, []);

  // ─── Métricas calculadas ───────────────────────────────────────────────────
  const totalVendido = vendas.reduce((acc, v) => acc + v.valorLiquido, 0);
  const totalTaxasCartao = vendas.reduce((acc, v) => acc + (v.valorTotal - v.valorLiquido), 0);
  const totalDespesas = despesas.reduce((acc, d) => acc + d.valor, 0);
  const totalAportes = aportes.reduce((acc, a) => acc + a.valor, 0);
  const totalRetiradas = retiradas.reduce((acc, r) => acc + r.valor, 0);
  const totalDescontos = vendas.reduce((acc, v) => acc + (v.valorDesconto ?? 0), 0);
  const lucroOperacional = totalVendido - totalDespesas;
  const capitalNaoRecuperado = Math.max(0, totalAportes - lucroOperacional - totalRetiradas);
  const retiradaSegura = Math.max(0, lucroOperacional - totalRetiradas);

  type StatusLoja = 'dependente' | 'equilibrio' | 'lucrativa';
  let statusLoja: StatusLoja = 'dependente';
  if (lucroOperacional >= totalAportes) statusLoja = 'lucrativa';
  else if (lucroOperacional >= totalDespesas) statusLoja = 'equilibrio';

  // ─── CRUD Categorias ───────────────────────────────────────────────────────
  const addCategoria = useCallback(async (nome: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data: row, error } = await supabase.from('categorias').insert({
      user_id: user.id,
      nome: nome.trim(),
    }).select().maybeSingle();
    if (!error && row) setCategorias(prev => [...prev, rowToCategoria(row as Record<string, unknown>)].sort((a, b) => a.nome.localeCompare(b.nome)));
  }, []);

  const updateCategoria = useCallback(async (id: string, nome: string) => {
    const { error } = await supabase.from('categorias').update({ nome: nome.trim() }).eq('id', id);
    if (!error) setCategorias(prev => prev.map(c => c.id === id ? { ...c, nome: nome.trim() } : c).sort((a, b) => a.nome.localeCompare(b.nome)));
  }, []);

  const deleteCategoria = useCallback(async (id: string) => {
    const { error } = await supabase.from('categorias').delete().eq('id', id);
    if (!error) setCategorias(prev => prev.filter(c => c.id !== id));
  }, []);

  // ─── CRUD Clientes ──────────────────────────────────────────────────────────
  const addCliente = useCallback(async (data: Omit<Cliente, 'id'>) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data: row, error } = await supabase.from('clientes').insert({
      user_id: user.id,
      nome: data.nome.trim(),
      telefone: data.telefone?.trim() ?? '',
    }).select().maybeSingle();
    if (!error && row) setClientes(prev => [...prev, rowToCliente(row as Record<string, unknown>)].sort((a, b) => a.nome.localeCompare(b.nome)));
  }, []);

  const updateCliente = useCallback(async (id: string, data: Partial<Cliente>) => {
    const patch: Record<string, unknown> = {};
    if (data.nome !== undefined) patch.nome = data.nome.trim();
    if (data.telefone !== undefined) patch.telefone = data.telefone?.trim() ?? '';
    const { error } = await supabase.from('clientes').update(patch).eq('id', id);
    if (!error) setClientes(prev => prev.map(c => c.id === id ? { ...c, ...data } : c).sort((a, b) => a.nome.localeCompare(b.nome)));
  }, []);

  const deleteCliente = useCallback(async (id: string) => {
    const { error } = await supabase.from('clientes').delete().eq('id', id);
    if (!error) setClientes(prev => prev.filter(c => c.id !== id));
  }, []);

  // ─── CRUD Produtos ─────────────────────────────────────────────────────────
  const addProduto = useCallback(async (data: Omit<Produto, 'id' | 'criadoEm'>) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data: row, error } = await supabase.from('produtos').insert({
      user_id: user.id,
      nome: data.nome,
      categoria: data.categoria,
      custo: data.custo,
      custo_extra: data.custoExtra,
      preco_venda: data.precoVenda,
      margem: data.margem,
      estoque: data.estoque,
      status: data.status,
    }).select().maybeSingle();
    if (!error && row) setProdutos(prev => [rowToProduto(row as Record<string, unknown>), ...prev]);
  }, []);

  const updateProduto = useCallback(async (id: string, data: Partial<Produto>) => {
    const patch: Record<string, unknown> = {};
    if (data.nome !== undefined) patch.nome = data.nome;
    if (data.categoria !== undefined) patch.categoria = data.categoria;
    if (data.custo !== undefined) patch.custo = data.custo;
    if (data.custoExtra !== undefined) patch.custo_extra = data.custoExtra;
    if (data.precoVenda !== undefined) patch.preco_venda = data.precoVenda;
    if (data.margem !== undefined) patch.margem = data.margem;
    if (data.estoque !== undefined) patch.estoque = data.estoque;
    if (data.status !== undefined) patch.status = data.status;
    const { error } = await supabase.from('produtos').update(patch).eq('id', id);
    if (!error) setProdutos(prev => prev.map(p => p.id === id ? { ...p, ...data } : p));
  }, []);

  const deleteProduto = useCallback(async (id: string) => {
    const { error } = await supabase.from('produtos').delete().eq('id', id);
    if (!error) setProdutos(prev => prev.filter(p => p.id !== id));
  }, []);

  // ─── CRUD Vendas ───────────────────────────────────────────────────────────
  const addVenda = useCallback(async (data: Omit<Venda, 'id'>) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const descontoValor = data.descontoPercentual ? (data.valorTotal * data.descontoPercentual) / 100 : 0;
    const valorAposDesconto = data.valorTotal - descontoValor;
    const taxaValor = data.formaPagamento === 'cartao' ? (valorAposDesconto * data.taxaCartao) / 100 : 0;
    const valorLiquido = valorAposDesconto - taxaValor;

    const { data: row, error } = await supabase.from('vendas').insert({
      user_id: user.id,
      produto_id: data.produtoId || null,
      produto_nome: data.produtoNome,
      quantidade: data.quantidade,
      valor_total: data.valorTotal,
      forma_pagamento: data.formaPagamento,
      taxa_cartao: data.formaPagamento === 'cartao' ? data.taxaCartao : 0,
      valor_liquido: valorLiquido,
      data: data.data,
      cliente_id: data.clienteId || null,
      cliente_nome: data.clienteNome || null,
      desconto_percentual: data.descontoPercentual ?? 0,
      valor_desconto: descontoValor,
    }).select().maybeSingle();

    if (!error && row) {
      setVendas(prev => [rowToVenda(row as Record<string, unknown>), ...prev]);
      const produto = produtos.find(p => p.id === data.produtoId);
      if (produto) {
        const novoEstoque = Math.max(0, produto.estoque - data.quantidade);
        await updateProduto(data.produtoId, { estoque: novoEstoque });
      }
    }
  }, [produtos, updateProduto]);

  const deleteVenda = useCallback(async (id: string) => {
    const { error } = await supabase.from('vendas').delete().eq('id', id);
    if (!error) setVendas(prev => prev.filter(v => v.id !== id));
  }, []);

  const updateVenda = useCallback(async (id: string, data: Partial<Venda>) => {
    const patch: Record<string, unknown> = {};
    if (data.produtoId !== undefined) patch.produto_id = data.produtoId || null;
    if (data.produtoNome !== undefined) patch.produto_nome = data.produtoNome;
    if (data.quantidade !== undefined) patch.quantidade = data.quantidade;
    if (data.valorTotal !== undefined) patch.valor_total = data.valorTotal;
    if (data.formaPagamento !== undefined) patch.forma_pagamento = data.formaPagamento;
    if (data.taxaCartao !== undefined) patch.taxa_cartao = data.taxaCartao;
    if (data.valorLiquido !== undefined) patch.valor_liquido = data.valorLiquido;
    if (data.data !== undefined) patch.data = data.data;
    if (data.clienteId !== undefined) patch.cliente_id = data.clienteId || null;
    if (data.clienteNome !== undefined) patch.cliente_nome = data.clienteNome || null;
    if (data.descontoPercentual !== undefined) patch.desconto_percentual = data.descontoPercentual ?? 0;
    if (data.valorDesconto !== undefined) patch.valor_desconto = data.valorDesconto ?? 0;
    const { error } = await supabase.from('vendas').update(patch).eq('id', id);
    if (!error) setVendas(prev => prev.map(v => v.id === id ? { ...v, ...data } : v));
  }, []);

  // ─── CRUD Despesas ─────────────────────────────────────────────────────────
  const addDespesa = useCallback(async (data: Omit<Despesa, 'id'>) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data: row, error } = await supabase.from('despesas').insert({
      user_id: user.id,
      descricao: data.descricao,
      valor: data.valor,
      categoria: data.categoria,
      data: data.data,
    }).select().maybeSingle();
    if (!error && row) setDespesas(prev => [rowToDespesa(row as Record<string, unknown>), ...prev]);
  }, []);

  const deleteDespesa = useCallback(async (id: string) => {
    const { error } = await supabase.from('despesas').delete().eq('id', id);
    if (!error) setDespesas(prev => prev.filter(d => d.id !== id));
  }, []);

  const updateDespesa = useCallback(async (id: string, data: Partial<Despesa>) => {
    const patch: Record<string, unknown> = {};
    if (data.descricao !== undefined) patch.descricao = data.descricao;
    if (data.valor !== undefined) patch.valor = data.valor;
    if (data.categoria !== undefined) patch.categoria = data.categoria;
    if (data.data !== undefined) patch.data = data.data;
    const { error } = await supabase.from('despesas').update(patch).eq('id', id);
    if (!error) setDespesas(prev => prev.map(d => d.id === id ? { ...d, ...data } : d));
  }, []);

  // ─── CRUD Aportes ──────────────────────────────────────────────────────────
  const addAporte = useCallback(async (data: Omit<Aporte, 'id'>) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data: row, error } = await supabase.from('aportes').insert({
      user_id: user.id,
      valor: data.valor,
      data: data.data,
      observacao: data.observacao,
    }).select().maybeSingle();
    if (!error && row) setAportes(prev => [rowToAporte(row as Record<string, unknown>), ...prev]);
  }, []);

  const deleteAporte = useCallback(async (id: string) => {
    const { error } = await supabase.from('aportes').delete().eq('id', id);
    if (!error) setAportes(prev => prev.filter(a => a.id !== id));
  }, []);

  const updateAporte = useCallback(async (id: string, data: Partial<Aporte>) => {
    const patch: Record<string, unknown> = {};
    if (data.valor !== undefined) patch.valor = data.valor;
    if (data.data !== undefined) patch.data = data.data;
    if (data.observacao !== undefined) patch.observacao = data.observacao;
    const { error } = await supabase.from('aportes').update(patch).eq('id', id);
    if (!error) setAportes(prev => prev.map(a => a.id === id ? { ...a, ...data } : a));
  }, []);

  // ─── CRUD Retiradas ────────────────────────────────────────────────────────
  const addRetirada = useCallback(async (data: Omit<Retirada, 'id'>) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data: row, error } = await supabase.from('retiradas').insert({
      user_id: user.id,
      valor: data.valor,
      data: data.data,
    }).select().maybeSingle();
    if (!error && row) setRetiradas(prev => [rowToRetirada(row as Record<string, unknown>), ...prev]);
  }, []);

  const deleteRetirada = useCallback(async (id: string) => {
    const { error } = await supabase.from('retiradas').delete().eq('id', id);
    if (!error) setRetiradas(prev => prev.filter(r => r.id !== id));
  }, []);

  const updateRetirada = useCallback(async (id: string, data: Partial<Retirada>) => {
    const patch: Record<string, unknown> = {};
    if (data.valor !== undefined) patch.valor = data.valor;
    if (data.data !== undefined) patch.data = data.data;
    const { error } = await supabase.from('retiradas').update(patch).eq('id', id);
    if (!error) setRetiradas(prev => prev.map(r => r.id === id ? { ...r, ...data } : r));
  }, []);

  // ─── CRUD RetiradaProdutos ─────────────────────────────────────────────────
  const addRetiradaProduto = useCallback(async (data: Omit<RetiradaProduto, 'id'>) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data: row, error } = await supabase.from('retiradas_produto').insert({
      user_id: user.id,
      produto_id: data.produtoId || null,
      produto_nome: data.produtoNome,
      quantidade: data.quantidade,
      custo_unitario: data.custoUnitario,
      custo_total: data.custoTotal,
      motivo: data.motivo,
      data: data.data,
    }).select().maybeSingle();
    if (!error && row) {
      setRetiradaProdutos(prev => [rowToRetiradaProduto(row as Record<string, unknown>), ...prev]);
      // Atualiza estoque do produto
      const produto = produtos.find(p => p.id === data.produtoId);
      if (produto) {
        const novoEstoque = Math.max(0, produto.estoque - data.quantidade);
        await updateProduto(data.produtoId, { estoque: novoEstoque });
      }
      // Cria despesa automática representando o custo da retirada
      const { data: despRow } = await supabase.from('despesas').insert({
        user_id: user.id,
        descricao: `Retirada: ${data.produtoNome} (${data.quantidade}x) — ${data.motivo}`,
        valor: data.custoTotal,
        categoria: 'Retirada de Produto',
        data: data.data,
      }).select().maybeSingle();
      if (despRow) setDespesas(prev => [rowToDespesa(despRow as Record<string, unknown>), ...prev]);
    }
  }, [produtos, updateProduto]);

  const updateRetiradaProduto = useCallback(async (id: string, data: Partial<RetiradaProduto>) => {
    const patch: Record<string, unknown> = {};
    if (data.produtoId !== undefined) patch.produto_id = data.produtoId || null;
    if (data.produtoNome !== undefined) patch.produto_nome = data.produtoNome;
    if (data.quantidade !== undefined) patch.quantidade = data.quantidade;
    if (data.custoUnitario !== undefined) patch.custo_unitario = data.custoUnitario;
    if (data.custoTotal !== undefined) patch.custo_total = data.custoTotal;
    if (data.motivo !== undefined) patch.motivo = data.motivo;
    if (data.data !== undefined) patch.data = data.data;
    const { error } = await supabase.from('retiradas_produto').update(patch).eq('id', id);
    if (!error) setRetiradaProdutos(prev => prev.map(r => r.id === id ? { ...r, ...data } : r));
  }, []);

  const deleteRetiradaProduto = useCallback(async (id: string) => {
    // Busca a retirada antes de excluir para devolver estoque e remover despesa
    const retirada = retiradaProdutos.find(r => r.id === id);
    const { error } = await supabase.from('retiradas_produto').delete().eq('id', id);
    if (!error) {
      setRetiradaProdutos(prev => prev.filter(r => r.id !== id));

      // Devolve as unidades ao estoque do produto
      if (retirada?.produtoId) {
        const produto = produtos.find(p => p.id === retirada.produtoId);
        if (produto) {
          const estoqueDevolvido = produto.estoque + retirada.quantidade;
          await updateProduto(retirada.produtoId, { estoque: estoqueDevolvido });
        }
      }

      // Remove a despesa gerada automaticamente para essa retirada
      if (retirada) {
        const descricaoEsperada = `Retirada: ${retirada.produtoNome} (${retirada.quantidade}x) — ${retirada.motivo}`;
        const { data: rows } = await supabase
          .from('despesas')
          .select('id')
          .eq('categoria', 'Retirada de Produto')
          .eq('descricao', descricaoEsperada)
          .eq('data', retirada.data)
          .eq('valor', retirada.custoTotal)
          .limit(1);
        if (rows && rows.length > 0) {
          const despId = rows[0].id as string;
          await supabase.from('despesas').delete().eq('id', despId);
          setDespesas(prev => prev.filter(d => d.id !== despId));
        }
      }
    }
  }, [retiradaProdutos, produtos, updateProduto]);

  return {
    produtos,
    clientes,
    vendas,
    despesas,
    aportes,
    retiradas,
    retiradaProdutos,
    categorias,
    loadingData,
    supabaseReady,
    totalVendido,
    totalTaxasCartao,
    totalDespesas,
    totalAportes,
    totalRetiradas,
    totalDescontos,
    lucroOperacional,
    capitalNaoRecuperado,
    retiradaSegura,
    statusLoja,
    addCategoria, updateCategoria, deleteCategoria,
    addCliente, updateCliente, deleteCliente,
    addProduto, updateProduto, deleteProduto,
    addVenda, updateVenda, deleteVenda,
    addDespesa, updateDespesa, deleteDespesa,
    addAporte, updateAporte, deleteAporte,
    addRetirada, updateRetirada, deleteRetirada,
    addRetiradaProduto, updateRetiradaProduto, deleteRetiradaProduto,
  };
}
